import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
 collection, query, where, getDocs, orderBy, limit, addDoc, updateDoc, doc, arrayUnion
} from "firebase/firestore";

const SPORTS = ["전체", "탁구", "배드민턴", "테니스", "볼링", "축구", "농구", "배구", "야구"];
const STATUS_FILTER = ["전체", "모집중", "진행중", "완료"];

function TournamentList() {
 const navigate = useNavigate();
 const user = JSON.parse(localStorage.getItem("user"));
 
 const [tournaments, setTournaments] = useState([]);
 const [filteredTournaments, setFilteredTournaments] = useState([]);
 const [loading, setLoading] = useState(true);
 const [registering, setRegistering] = useState(null);
 const [filters, setFilters] = useState({
   sport: "전체",
   status: "전체",
   search: ""
 });
 const [pendingTournaments, setPendingTournaments] = useState([]);

 useEffect(() => {
   fetchTournaments();
 }, []);

 useEffect(() => {
   applyFilters();
 }, [tournaments, filters]);

 useEffect(() => {
   const fetchPending = async () => {
     if (!user) return;
     const q = query(
       collection(db, "tournamentRegistrations"),
       where("userId", "==", user.id),
       where("status", "==", "pending")
     );
     const snap = await getDocs(q);
     setPendingTournaments(snap.docs.map(doc => doc.data().tournamentId));
   };
   fetchPending();
 }, [user]);

 const fetchTournaments = async () => {
   try {
     const q = query(
       collection(db, "tournaments"),
       where("isPublic", "==", true),
       orderBy("createdAt", "desc")
     );
     
     const snapshot = await getDocs(q);
     const tournamentsData = snapshot.docs.map(doc => ({
       id: doc.id,
       ...doc.data()
     }));
     
     setTournaments(tournamentsData);
   } catch (error) {
     console.error("대회 목록 로딩 실패:", error);
   } finally {
     setLoading(false);
   }
 };

 const applyFilters = () => {
   let filtered = [...tournaments];

   // 종목 필터
   if (filters.sport !== "전체") {
     filtered = filtered.filter(t => t.sport === filters.sport);
   }

   // 상태 필터
   if (filters.status !== "전체") {
     const statusMap = {
       "모집중": "registration",
       "진행중": "active", 
       "완료": "completed"
     };
     filtered = filtered.filter(t => t.status === statusMap[filters.status]);
   }

   // 검색 필터
   if (filters.search.trim()) {
     const searchTerm = filters.search.toLowerCase();
     filtered = filtered.filter(t => 
       t.name.toLowerCase().includes(searchTerm) ||
       t.organizerName.toLowerCase().includes(searchTerm) ||
       t.description.toLowerCase().includes(searchTerm)
     );
   }

   setFilteredTournaments(filtered);
 };

 const handleRegister = async (tournament) => {
   if (!user) {
     alert("로그인이 필요합니다.");
     navigate('/login');
     return;
   }

   if (!canRegister(tournament)) {
     alert("참가 신청할 수 없습니다.");
     return;
   }

   setRegistering(tournament.id);
   
   try {
     // 대회 참가자 목록에 추가
     await updateDoc(doc(db, "tournaments", tournament.id), {
       participants: arrayUnion({
         userId: user.id,
         nickname: user.nickname || user.id,
         registeredAt: new Date(),
         status: 'registered'
       })
     });

     // 알림 생성 (대회 주최자에게)
     await addDoc(collection(db, "notifications"), {
       type: "tournament_registration",
       from: user.id,
       fromNickname: user.nickname || user.id,
       to: tournament.organizerId,
       tournamentId: tournament.id,
       tournamentName: tournament.name,
       status: "unread",
       createdAt: new Date(),
       message: `${user.nickname || user.id}님이 ${tournament.name} 대회에 참가 신청했습니다.`
     });

     alert("참가 신청이 완료되었습니다!");
     fetchTournaments(); // 목록 새로고침
     
   } catch (error) {
     console.error("참가 신청 실패:", error);
     alert("참가 신청 중 오류가 발생했습니다.");
   } finally {
     setRegistering(null);
   }
 };

 const getStatusInfo = (status, registrationEnd) => {
   const statusConfig = {
     'registration': { 
       label: '모집중', 
       color: '#10b981', 
       bg: '#d1fae5',
       icon: '📝'
     },
     'active': { 
       label: '진행중', 
       color: '#3b82f6', 
       bg: '#dbeafe',
       icon: '⚡'
     },
     'completed': { 
       label: '완료', 
       color: '#6b7280', 
       bg: '#f3f4f6',
       icon: '✅'
     },
     'upcoming': { 
       label: '예정', 
       color: '#f59e0b', 
       bg: '#fef3c7',
       icon: '⏰'
     }
   };

   // 모집 마감일 체크
   if (status === 'registration' && registrationEnd) {
     const now = new Date();
     const endDate = registrationEnd.toDate ? registrationEnd.toDate() : new Date(registrationEnd);
     if (now > endDate) {
       return {
         label: '모집마감',
         color: '#ef4444',
         bg: '#fee2e2',
         icon: '🚫'
       };
     }
   }

   return statusConfig[status] || statusConfig.upcoming;
 };

 const formatDate = (date) => {
   if (!date) return '-';
   const d = date.toDate ? date.toDate() : new Date(date);
   return d.toLocaleDateString('ko-KR', {
     month: 'long',
     day: 'numeric'
   });
 };

 const getDaysUntil = (date) => {
   if (!date) return null;
   const d = date.toDate ? date.toDate() : new Date(date);
   const now = new Date();
   const diffTime = d - now;
   const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
   return diffDays;
 };

 const canRegister = (tournament) => {
   if (!user) return false;
   if (tournament.status !== 'registration') return false;
   if (tournament.participants?.some(p => p.userId === user.id)) return false;
   
   const now = new Date();
   const regEnd = tournament.registrationEnd?.toDate ? tournament.registrationEnd.toDate() : new Date(tournament.registrationEnd);
   return now <= regEnd;
 };

 const isRegistered = (tournament) => {
   return tournament.participants?.some(p => p.userId === user?.id);
 };

 const isPendingApproval = (tournament) => {
  return pendingTournaments.includes(tournament.id);
};

 if (loading) {
   return (
     <div style={{
       display: 'flex',
       justifyContent: 'center',
       alignItems: 'center',
       minHeight: '60vh',
       fontSize: 18,
       color: '#6b7280'
     }}>
       <div>🏆 대회 목록 로딩중...</div>
     </div>
   );
 }

 return (
   <div style={{
     maxWidth: 1200,
     margin: '40px auto',
     padding: '0 20px'
   }}>
     {/* 헤더 */}
     <div style={{
       background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
       borderRadius: 24,
       padding: '40px 32px',
       color: '#fff',
       marginBottom: 32,
       boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)'
     }}>
       <div style={{
         display: 'flex',
         justifyContent: 'space-between',
         alignItems: 'center'
       }}>
         <div>
           <h1 style={{
             fontSize: 36,
             fontWeight: 900,
             margin: 0,
             marginBottom: 8,
             textShadow: '0 2px 4px rgba(0,0,0,0.3)'
           }}>
             🏆 대회 목록
           </h1>
           <p style={{
             fontSize: 18,
             margin: 0,
             opacity: 0.9
           }}>
             다양한 스포츠 대회에 참가해보세요!
           </p>
         </div>
         
         {user?.userType === 'association' && (
           <button
             onClick={() => navigate('/association/create-tournament')}
             style={{
               background: 'rgba(255,255,255,0.2)',
               color: '#fff',
               border: '1px solid rgba(255,255,255,0.3)',
               borderRadius: 12,
               padding: '12px 24px',
               fontSize: 16,
               fontWeight: 600,
               cursor: 'pointer',
               transition: 'all 0.2s ease',
               backdropFilter: 'blur(10px)'
             }}
           >
             ➕ 새 대회 생성
           </button>
         )}
       </div>
       
       <div style={{
         display: 'grid',
         gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
         gap: 20,
         marginTop: 20,
         opacity: 0.9
       }}>
         <div>
           <div style={{ fontSize: 14, marginBottom: 4 }}>총 대회</div>
           <div style={{ fontSize: 24, fontWeight: 700 }}>{tournaments.length}</div>
         </div>
         <div>
           <div style={{ fontSize: 14, marginBottom: 4 }}>모집중</div>
           <div style={{ fontSize: 24, fontWeight: 700 }}>
             {tournaments.filter(t => t.status === 'registration').length}
           </div>
         </div>
         <div>
           <div style={{ fontSize: 14, marginBottom: 4 }}>진행중</div>
           <div style={{ fontSize: 24, fontWeight: 700 }}>
             {tournaments.filter(t => t.status === 'active').length}
           </div>
         </div>
         <div>
           <div style={{ fontSize: 14, marginBottom: 4 }}>내 참가</div>
           <div style={{ fontSize: 24, fontWeight: 700 }}>
             {user ? tournaments.filter(t => t.participants?.some(p => p.userId === user.id)).length : 0}
           </div>
         </div>
       </div>
     </div>

     {/* 필터 섹션 */}
     <div style={{
       background: '#fff',
       borderRadius: 20,
       padding: 24,
       marginBottom: 32,
       boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
       border: '1px solid #f1f5f9'
     }}>
       <div style={{
         display: 'grid',
         gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
         gap: 16,
         alignItems: 'end'
       }}>
         {/* 종목 필터 */}
         <div>
           <label style={{
             display: 'block',
             fontSize: 14,
             fontWeight: 600,
             color: '#374151',
             marginBottom: 8
           }}>
             종목
           </label>
           <select
             value={filters.sport}
             onChange={(e) => setFilters(prev => ({ ...prev, sport: e.target.value }))}
             style={{
               width: '100%',
               padding: '10px 12px',
               border: '1px solid #d1d5db',
               borderRadius: 8,
               fontSize: 14,
               background: '#fff'
             }}
           >
             {SPORTS.map(sport => (
               <option key={sport} value={sport}>{sport}</option>
             ))}
           </select>
         </div>

         {/* 상태 필터 */}
         <div>
           <label style={{
             display: 'block',
             fontSize: 14,
             fontWeight: 600,
             color: '#374151',
             marginBottom: 8
           }}>
             상태
           </label>
           <select
             value={filters.status}
             onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
             style={{
               width: '100%',
               padding: '10px 12px',
               border: '1px solid #d1d5db',
               borderRadius: 8,
               fontSize: 14,
               background: '#fff'
             }}
           >
             {STATUS_FILTER.map(status => (
               <option key={status} value={status}>{status}</option>
             ))}
           </select>
         </div>

         {/* 검색 */}
         <div>
           <label style={{
             display: 'block',
             fontSize: 14,
             fontWeight: 600,
             color: '#374151',
             marginBottom: 8
           }}>
             검색
           </label>
           <input
             type="text"
             placeholder="대회명, 주최자 검색..."
             value={filters.search}
             onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
             style={{
               width: '100%',
               padding: '10px 12px',
               border: '1px solid #d1d5db',
               borderRadius: 8,
               fontSize: 14
             }}
           />
         </div>

         {/* 초기화 버튼 */}
         <div>
           <button
             onClick={() => setFilters({ sport: "전체", status: "전체", search: "" })}
             style={{
               padding: '10px 20px',
               background: '#f3f4f6',
               color: '#374151',
               border: '1px solid #d1d5db',
               borderRadius: 8,
               fontSize: 14,
               fontWeight: 600,
               cursor: 'pointer'
             }}
           >
             🔄 초기화
           </button>
         </div>
       </div>
     </div>

     {/* 대회 목록 */}
     <div style={{
       display: 'grid',
       gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
       gap: 24
     }}>
       {filteredTournaments.length === 0 ? (
         <div style={{
           gridColumn: '1 / -1',
           textAlign: 'center',
           padding: 60,
           background: '#fff',
           borderRadius: 20,
           boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
         }}>
           <div style={{ fontSize: 64, marginBottom: 16 }}>🔍</div>
           <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, color: '#374151' }}>
             조건에 맞는 대회가 없습니다
           </h3>
           <p style={{ color: '#6b7280', fontSize: 16 }}>
             다른 필터 조건을 시도해보세요
           </p>
         </div>
       ) : (
         filteredTournaments.map(tournament => {
           const statusInfo = getStatusInfo(tournament.status, tournament.registrationEnd);
           const daysUntilStart = getDaysUntil(tournament.startDate);
           const daysUntilRegEnd = getDaysUntil(tournament.registrationEnd);
           const registered = isRegistered(tournament);
           
           return (
             <div
               key={tournament.id}
               style={{
                 background: '#fff',
                 borderRadius: 20,
                 padding: 24,
                 boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                 border: '1px solid #f1f5f9',
                 transition: 'all 0.3s ease',
                 cursor: 'pointer'
               }}
               onMouseOver={e => {
                 e.currentTarget.style.transform = 'translateY(-4px)';
                 e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
               }}
               onMouseOut={e => {
                 e.currentTarget.style.transform = 'translateY(0)';
                 e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
               }}
               onClick={() => navigate(`/tournament/${tournament.id}`)}
             >
               {/* 상태 배지 */}
               <div style={{
                 display: 'flex',
                 justifyContent: 'space-between',
                 alignItems: 'flex-start',
                 marginBottom: 16
               }}>
                 <span style={{
                   background: statusInfo.bg,
                   color: statusInfo.color,
                   padding: '6px 12px',
                   borderRadius: 20,
                   fontSize: 12,
                   fontWeight: 600,
                   display: 'flex',
                   alignItems: 'center',
                   gap: 4
                 }}>
                   <span>{statusInfo.icon}</span>
                   {statusInfo.label}
                 </span>
                 
                 <span style={{
                   background: '#f3f4f6',
                   color: '#6b7280',
                   padding: '4px 8px',
                   borderRadius: 8,
                   fontSize: 12,
                   fontWeight: 600
                 }}>
                   {tournament.sport}
                 </span>
               </div>

               {/* 대회 정보 */}
               <h3 style={{
                 fontSize: 18,
                 fontWeight: 700,
                 marginBottom: 8,
                 color: '#1f2937',
                 lineHeight: 1.3
               }}>
                 {tournament.name}
               </h3>

               <p style={{
                 color: '#6b7280',
                 fontSize: 14,
                 marginBottom: 16,
                 lineHeight: 1.4,
                 display: '-webkit-box',
                 WebkitLineClamp: 2,
                 WebkitBoxOrient: 'vertical',
                 overflow: 'hidden'
               }}>
                 {tournament.description}
               </p>

               {/* 주최자 정보 */}
               <div style={{
                 display: 'flex',
                 alignItems: 'center',
                 marginBottom: 16,
                 padding: '8px 12px',
                 background: '#f8fafc',
                 borderRadius: 8
               }}>
                 <span style={{ fontSize: 16, marginRight: 8 }}>🏢</span>
                 <span style={{
                   fontSize: 14,
                   fontWeight: 600,
                   color: '#374151'
                 }}>
                   {tournament.organizerName}
                 </span>
               </div>

               {/* 날짜 정보 */}
               <div style={{
                 display: 'grid',
                 gridTemplateColumns: '1fr 1fr',
                 gap: 12,
                 marginBottom: 16,
                 fontSize: 13,
                 color: '#6b7280'
               }}>
                 <div>
                   <span style={{ fontWeight: 600 }}>📅 대회일:</span><br/>
                   {formatDate(tournament.startDate)}
                   {daysUntilStart > 0 && (
                     <span style={{ color: '#f59e0b', fontWeight: 600, marginLeft: 4 }}>
                       (D-{daysUntilStart})
                     </span>
                   )}
                 </div>
                 <div>
                   <span style={{ fontWeight: 600 }}>⏰ 모집마감:</span><br/>
                   {formatDate(tournament.registrationEnd)}
                   {daysUntilRegEnd > 0 && (
                     <span style={{ color: '#ef4444', fontWeight: 600, marginLeft: 4 }}>
                       (D-{daysUntilRegEnd})
                     </span>
                   )}
                 </div>
               </div>

               {/* 참가자 정보 */}
               <div style={{
                 display: 'flex',
                 justifyContent: 'space-between',
                 alignItems: 'center',
                 marginBottom: 16,
                 padding: '8px 12px',
                 background: '#f0f9ff',
                 borderRadius: 8
               }}>
                 <span style={{
                   fontSize: 14,
                   color: '#0369a1',
                   fontWeight: 600
                 }}>
                   👥 참가자: {tournament.participants?.length || 0}명
                 </span>
                 <span style={{
                   fontSize: 12,
                   color: '#6b7280'
                 }}>
                   최대 {tournament.maxParticipants}명
                 </span>
               </div>

               {/* 참가비 */}
               {tournament.entryFee && (
                 <div style={{
                   fontSize: 14,
                   color: '#059669',
                   fontWeight: 600,
                   marginBottom: 16
                 }}>
                   💰 참가비: {parseInt(tournament.entryFee).toLocaleString()}원
                 </div>
               )}

               {/* 액션 버튼 */}
               <div style={{
                 display: 'flex',
                 gap: 8
               }}>
                 {registered ? (
                   <div style={{
                     flex: 1,
                     background: '#d1fae5',
                     color: '#065f46',
                     padding: '10px 16px',
                     borderRadius: 8,
                     fontSize: 14,
                     fontWeight: 600,
                     textAlign: 'center',
                     border: '1px solid #a7f3d0'
                   }}>
                     ✅ 참가 신청 완료
                   </div>
                 ) : isPendingApproval(tournament) ? (
                   <div style={{
                     flex: 1,
                     background: '#fef9c3',
                     color: '#b45309',
                     padding: '10px 16px',
                     borderRadius: 8,
                     fontSize: 14,
                     fontWeight: 600,
                     textAlign: 'center',
                     border: '1px solid #fde68a'
                   }}>
                     ⏳ 승인 대기중
                   </div>
                 ) : canRegister(tournament) && (!user || user.userType !== 'association') ? (
                   <button
                     onClick={(e) => {
                       e.stopPropagation();
                       navigate(`/tournament/${tournament.id}/register`);
                     }}
                     style={{
                       flex: 1,
                       background: '#667eea',
                       color: '#fff',
                       border: 'none',
                       padding: '10px 16px',
                       borderRadius: 8,
                       fontSize: 14,
                       fontWeight: 600,
                       cursor: 'pointer',
                       opacity: 1
                     }}
                   >
                     🎯 참가 신청
                   </button>
                 ) : (
                   <div style={{
                     flex: 1,
                     background: '#f3f4f6',
                     color: '#6b7280',
                     padding: '10px 16px',
                     borderRadius: 8,
                     fontSize: 14,
                     fontWeight: 600,
                     textAlign: 'center'
                   }}>
                     참가 불가
                   </div>
                 )}
                 
                 <button
                   onClick={(e) => {
                     e.stopPropagation();
                     navigate(`/tournament/${tournament.id}`);
                   }}
                   style={{
                     background: '#f8fafc',
                     color: '#374151',
                     border: '1px solid #d1d5db',
                     padding: '10px 16px',
                     borderRadius: 8,
                     fontSize: 14,
                     fontWeight: 600,
                     cursor: 'pointer'
                   }}
                 >
                   📋 상세보기
                 </button>
               </div>
             </div>
           );
         })
       )}
     </div>
   </div>
 );
}

export default TournamentList;